import { useState, useEffect, useMemo } from "react";
import { useLoaderData, useParams } from "react-router-dom";

import { useQueryParams } from "../../../../../../../hooks/useQueryParams";
import PaginationControls from "../../../../../../global/PaginationControls";

import Header from "./components/Header";
import ReportTable from "./components/ReportTable";

const normalizeFilters = (filters) => {
  if (filters instanceof URLSearchParams) {
    return filters;
  }
  if (!filters) {
    return new URLSearchParams();
  }
  if (typeof filters === "string") {
    const normalized = filters.startsWith("?") ? filters.slice(1) : filters;
    return new URLSearchParams(normalized);
  }
  if (typeof filters === "object") {
    const next = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        return;
      }
      if (Array.isArray(value)) {
        next.set(key, value.join(","));
      } else {
        next.set(key, String(value));
      }
    });
    return next;
  }
  return new URLSearchParams();
};

const ReportDetailContent = ({
  reportData,
  reportId,
  filters,
  filtersUrl = "",
  updateFilter,
  bulkUpdateFilters,
  setFilters,
  showHeader = true,
  showActions = true,
  showFilters = true,
  showPagination = true,
  showSearchBar = true,
  showTitle = true,
  tableSx = {},
  reportContainerSx = {},
}) => {
  const resolvedFilters = useMemo(() => normalizeFilters(filters), [filters]);
  const [drawerCloseSignal, setDrawerCloseSignal] = useState(0);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const urlLimit = Number.parseInt(resolvedFilters?.get("limit") ?? "", 10);
  const urlOffset = Number.parseInt(resolvedFilters?.get("offset") ?? "", 10);
  const effectiveLimit =
    Number.isFinite(urlLimit) && urlLimit > 0 ? urlLimit : reportData?.defaultLimit || 50;
  const effectiveOffset = Number.isFinite(urlOffset) && urlOffset >= 0 ? urlOffset : 0;
  const currentPage = Math.floor(effectiveOffset / effectiveLimit) + 1;
  const [reportTotalCount, setReportTotalCount] = useState(reportData?.count || 0);
  function handlePageChange(_, value) {
    setIsApplyingFilters(true);
    updateFilter("offset", value ? (value - 1) * effectiveLimit : 0);
  }
  useEffect(() => {
    if (!reportData) {
      return;
    }
    const hasLimit = resolvedFilters.get("limit");
    const hasOffset = resolvedFilters.get("offset");

    if (!hasLimit || !hasOffset) {
      const next = new URLSearchParams(resolvedFilters);
      const nextLimit = String(reportData?.defaultLimit || 100);
      if (!hasLimit) {
        next.set("limit", nextLimit);
      }
      if (!hasOffset) {
        next.set("offset", "0");
      }
      if (typeof setFilters === "function") {
        setFilters(next);
      } else {
        if (!hasLimit && typeof updateFilter === "function") {
          updateFilter("limit", nextLimit, null, true);
        }
        if (!hasOffset && typeof updateFilter === "function") {
          updateFilter("offset", "0", null, true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData]);

  return (
    <div id="Report-Detail" data-testid="Report" className="h-[calc(100vh - 120px)]">
      <div
        style={reportContainerSx}
        className="relative h-auto max-h-[calc(100vh-90px)] rounded-md bg-white p-4 shadow-md"
      >
        {showHeader && (
          <Header
            reportData={reportData}
            reportId={reportId}
            filters={resolvedFilters}
            filtersUrl={filtersUrl}
            updateFilter={updateFilter}
            bulkUpdateFilters={bulkUpdateFilters}
            showActions={showActions}
            showFilters={showFilters}
            showSearchBar={showSearchBar}
            showTitle={showTitle}
            availableColumns={reportData?.availableColumns || []}
            drawerCloseSignal={drawerCloseSignal}
            setDrawerCloseSignal={setDrawerCloseSignal}
            onApplyStart={() => setIsApplyingFilters(true)}
          />
        )}
        <ReportTable
          reportId={reportId}
          tableSx={tableSx}
          reportData={reportData}
          filters={resolvedFilters}
          updateFilter={updateFilter}
          setReportTotalCount={setReportTotalCount}
          externalLoading={isApplyingFilters}
          onDataLoaded={() => setIsApplyingFilters(false)}
        />

        {showPagination && (
          <div className="flex w-full justify-center">
            <PaginationControls
              currentPage={currentPage}
              resultCount={{ total: reportTotalCount }}
              handleChange={handlePageChange}
              limit={effectiveLimit}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const RouteReportDetail = ({ showActions, showFilters, showPagination, showSearchBar }) => {
  const { reportData } = useLoaderData();
  const { id: reportId } = useParams();
  const { filters, updateFilter, setFilters, bulkUpdateFilters } = useQueryParams();
  return (
    <ReportDetailContent
      reportData={reportData}
      reportId={reportId}
      filters={filters}
      updateFilter={updateFilter}
      bulkUpdateFilters={bulkUpdateFilters}
      showActions={showActions}
      setFilters={setFilters}
      showFilters={showFilters}
      showPagination={showPagination}
      showSearchBar={showSearchBar}
    />
  );
};

const StandaloneReportDetail = ({
  bulkUpdateFilters,
  filters,
  filtersUrl,
  reportData,
  reportId,
  showActions,
  setFilters,
  showFilters,
  showPagination,
  showSearchBar,
  showTitle,
  updateFilter,
}) => {
  return (
    <ReportDetailContent
      bulkUpdateFilters={bulkUpdateFilters}
      filters={filters}
      filtersUrl={filtersUrl}
      reportData={reportData}
      reportId={reportId}
      setFilters={setFilters}
      showActions={showActions}
      showFilters={showFilters}
      showPagination={showPagination}
      showSearchBar={showSearchBar}
      showTitle={showTitle}
      updateFilter={updateFilter}
      tableSx={{ height: "100% !important" }}
      reportContainerSx={{ boxShadow: "none", padding: 0 }}
      showHeader={showActions || showFilters || showSearchBar}
    />
  );
};

const ReportDetail = ({
  bulkUpdateFilters,
  filters,
  filtersUrl,
  initialFilters,
  reportData,
  reportId,
  setFilters,
  showActions = true,
  showFilters = true,
  showPagination = true,
  showSearchBar = true,
  showTitle = true,
  syncQueryParams = true,
  updateFilter,
}) => {
  if (!syncQueryParams) {
    return (
      <StandaloneReportDetail
        bulkUpdateFilters={bulkUpdateFilters}
        filters={filters}
        filtersUrl={filtersUrl}
        initialFilters={initialFilters}
        reportData={reportData}
        reportId={reportId}
        setFilters={setFilters}
        showActions={showActions}
        showFilters={showFilters}
        showPagination={showPagination}
        showSearchBar={showSearchBar}
        showTitle={showTitle}
        updateFilter={updateFilter}
      />
    );
  }

  return (
    <RouteReportDetail
      showActions={showActions}
      showFilters={showFilters}
      showPagination={showPagination}
      showSearchBar={showSearchBar}
    />
  );
};

export default ReportDetail;
